import { useEffect, RefObject } from "react";

/**
 * Hook for detecting clicks outside a specified element.
 *
 * Handles both mouse and touch events. Intelligently ignores clicks inside
 * Mantine dropdowns and portals to prevent false positives.
 *
 * @template T - The HTML element type
 * @param ref - React ref to the element to watch for outside clicks
 * @param handler - Callback function triggered when click occurs outside
 *
 * @example
 * ```tsx
 * function Dropdown() {
 *   const ref = useRef<HTMLDivElement>(null);
 *   const [isOpen, setIsOpen] = useState(false);
 *
 *   useOnClickOutside(ref, () => setIsOpen(false));
 *
 *   return (
 *     isOpen && (
 *       <div ref={ref}>
 *         <p>Click outside to close</p>
 *       </div>
 *     )
 *   );
 * }
 * ```
 */
export function useOnClickOutside<T extends HTMLElement | null>(
  ref: RefObject<T> | null,
  handler: (event: MouseEvent | TouchEvent) => void
): void {
  useEffect(() => {
    if (!ref) {
      return;
    }
    const listener = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      // Don't trigger if click is inside the ref element
      if (!ref.current || ref.current.contains(target)) {
        return;
      }

      // Don't trigger if click is inside a Mantine dropdown portal
      // Mantine dropdowns are typically rendered in portals with specific classes
      if (target && target instanceof Element) {
        // Check if the clicked element or its parents have Mantine dropdown classes
        const mantineDropdown =
          target.closest("[data-mantine-dropdown-body]") ||
          target.closest(".mantine-Select-dropdown") ||
          target.closest(".mantine-MultiSelect-dropdown") ||
          target.closest("[data-portal]");

        if (mantineDropdown) {
          return;
        }
      }

      handler(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}
